import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { faVideo } from '@fortawesome/pro-regular-svg-icons/faVideo';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons/faFileLines';
import { faSitemap } from '@fortawesome/pro-regular-svg-icons/faSitemap';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Checkbox } from '@osmosis/tailwind-ui/components/Checkbox';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { A } from '@osmosis/tailwind-ui/components/Text';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import BreadcrumbsV2 from '../BreadcrumbsV2/BreadcrumbsV2';
import ItemThumbnail from '../video/ItemThumbnail';
import AddToPlaylistButton from '../../app/_components/playlist/AddToPlaylistButton';
import { AnimatePresence, useInView } from 'motion/react';
import * as motion from 'motion/react-m';
import { LibraryTopicGroupPage } from '../../types/library';
import Image from 'next/image';
import { useFetch } from '../../context/FetchProvider';
import { toggleItemChecked } from '../../lib/frontend/library/api';
import { assets } from '@osmosis/constants';
import { track } from '../../lib/util/tracking';
import { getBreadcrumbTrackHandler } from '../../lib/tracking';
import LockIcon from '../search/LockIcon';
import { useLibraryUserDataContext } from '../../context/LibraryUserDataProvider';
import { useLanguageContext } from '../../context/LanguageProvider';
import Skeleton from 'react-loading-skeleton';
import { text4, text5, textBlue } from '@osmosis/tailwind-ui/styles';
import { replaceS3ToCloudfront } from '../../lib/util/url';
import MotionDiv from '@osmosis/nextjs-components/MotionDiv';
import { useContentSize } from '@osmosis/nextjs-components/context';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

type Props = {
  data: LibraryTopicGroupPage;
};

const iconMap: Record<string, IconDefinition> = {
  notes: faFileLines,
  'yt-osm': faVideo,
  dmt: faSitemap,
};

/**
 * LibraryItemRow.
 */
const LibraryItemRow = (props: Props) => {
  const [isBreadcrumbsOpen, toggleIsBreadcrumbs] = useState(false);
  const {
    title,
    thumbnail,
    breadcrumbsv2,
    pageType,
    duration,
    resources,
    url: initialUrl,
    learnNodeID,
    mediaNodeID,
    tags = [],
    accessState,
  } = props.data;
  const { activeLanguage } = useLanguageContext();
  const url =
    activeLanguage && activeLanguage !== 'en'
      ? initialUrl.replace('/learn/', `/learn/${activeLanguage}/`)
      : initialUrl;
  const { formatMessage } = useIntl();

  const toBeRetiredTag = useMemo(
    () => tags.find(({ tag }) => tag.toLowerCase() === 'to be retired'),
    [tags],
  );

  const osmosisResources = resources.filter(
    (res) => res.name.includes('notes') || res.name.includes('yt-osm'),
  );

  const { learnWatchedItems, handleIsChecked } = useLibraryUserDataContext();

  const learnWatchedItem = useMemo(
    () => learnWatchedItems.find((m) => m.mediaID === mediaNodeID),
    [learnWatchedItems, mediaNodeID],
  );

  let lockedSubProduct;
  if (accessState) {
    const accessStateValues = Object.values(accessState);

    if (
      accessStateValues.length === 1 &&
      accessStateValues[0].state === 'locked'
    ) {
      lockedSubProduct = accessStateValues[0].groupName
        .replace(' ', '_')
        .toLowerCase();
    }
  }

  const [isChecked, setIsChecked] = useState(handleIsChecked(learnNodeID));

  const ref = useRef<HTMLLIElement>(null);

  const isInView = useInView(ref, { once: true });

  const { post } = useFetch();

  const handleTrackBreadcrumb = getBreadcrumbTrackHandler('library-page');

  const playIcon = pageType === 'video';

  const handleBreadcrumbsClick = () => {
    toggleIsBreadcrumbs((prev) => !prev);
  };

  const handleItemClick = useCallback(() => {
    const path = window.location.pathname;
    const levels = path.split('/');
    track(
      'library_interaction',
      {
        action: 'click_concept',
        version: 'reorg',
        breadcrumbs: path,
        topic: levels[levels.length - 1],
        concept: title,
      },
      ['keen'],
    );
  }, [title]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('click', handleItemClick);
    return () => el.removeEventListener('click', handleItemClick);
  }, [handleItemClick]);

  const handleToggleCheckbox = () => {
    setIsChecked(!isChecked);
    toggleItemChecked(post, learnNodeID, !isChecked);
  };

  const { getClassValue } = useContentSize();

  const playlistResponsiveClasses = getClassValue({
    defaultClasses: 'sm:hidden md:hidden lg:block',
    breakpoints: {
      sm: 'hidden',
      md: 'hidden',
      lg: 'block',
    },
  });

  const liResponsiveClasses = getClassValue({
    defaultClasses:
      'sm:min-h-100 md:min-h-108 sm:max-w-full md:max-w-full lg:max-w-[calc(50%-6px)]',
    breakpoints: {
      sm: 'min-h-100 max-w-full',
      md: 'min-h-108 max-w-full',
      lg: 'min-h-108 max-w-[calc(50%-6px)]',
    },
  });

  const thumbnailLinkResponsiveClasses = getClassValue({
    defaultClasses:
      'h-14 w-24 min-w-24 md:h-[72px] md:w-[129px] md:min-w-[129px]',
    breakpoints: {
      md: 'h-[72px] w-[129px] min-w-[129px]',
      base: 'h-14 w-24 min-w-24',
    },
  });

  const breadcrumbsSectionDesktopClasses = getClassValue({
    defaultClasses: 'hidden sm:block xl:block',
    breakpoints: {
      sm: 'block',
      xl: 'block',
      base: 'hidden',
    },
  });

  const breadcrumbsSectionMobileClasses = getClassValue({
    defaultClasses: 'block sm:hidden xl:hidden',
    breakpoints: {
      sm: 'hidden',
      xl: 'hidden',
      base: 'block',
    },
  });

  const isNoteThumbnail = thumbnail === assets.note;
  const thumbnailUrl =
    pageType === 'notes' ? thumbnail : replaceS3ToCloudfront(thumbnail) || '';

  const resourceItems = useMemo(() => {
    return osmosisResources?.map((res, index) => {
      const icon = iconMap[res.name];
      const ariaLabel = `${res.hint}${formatMessage({
        id: 'library.learn.item-row.resources-icon-hint.this-concept',
        defaultMessage: ' this concept',
      })}`;
      const dataTooltipID = `resource-tooltip-${index}-${learnNodeID}`;
      return (
        <Box
          key={res.name + index.toString()}
          data-testid="resources-icon-container"
          className={`z-10 mr-1 grayscale group-hover:grayscale-0`}
          onClick={(e) => e.stopPropagation()}
        >
          {icon ? (
            <Icon
              icon={icon}
              className={`mr-2 text-xs ${text4}`}
              aria-label={ariaLabel}
              data-tooltip-id={dataTooltipID}
            />
          ) : (
            <Image
              src={res.src}
              alt={res.name}
              aria-label={ariaLabel}
              width={12}
              height={14}
              data-tooltip-id={dataTooltipID}
            />
          )}

          <Tooltip
            id={dataTooltipID}
            content={`${res.hint} this concept`}
            place="bottom-start"
          />
        </Box>
      );
    });
  }, [osmosisResources, formatMessage, learnNodeID]);

  const playlistButton = (
    <Box
      data-testid="item-row-AddToPlaylistButton-container"
      className={cn(
        'absolute right-2.5 top-2.5 focus-within:block',
        playlistResponsiveClasses,
      )}
    >
      <AddToPlaylistButton
        isIconButton={true}
        items={[{ learnNodeID, name: title }]}
        data-testid="library-add-to-playlist-button"
        iconButtonProps={{
          variant: 'outline',
          color: 'neutral',
          className:
            'text-base text-lg text-normal transition duration-100 hover:bg-blue-600 hover:border-blue-600  hover:text-white [&_svg]:fill-neutral-500 [&:hover_svg]:fill-white',
        }}
      />
    </Box>
  );
  const showBreadcrumbsDropdown = (breadcrumbsv2?.length || 0) > 1 && isInView;
  const breadcrumbsDropdown = showBreadcrumbsDropdown ? (
    <>
      <Button
        data-testid="breadcrumbs-dropdown-container"
        className={` -mt-1 flex items-end justify-center px-1 py-1 sm:text-xs ${text4} border-none, group bg-transparent font-normal hover:bg-transparent hover:text-gray-700 active:bg-transparent `}
        aria-label={formatMessage(
          {
            id: 'library.library-item-row.breadcrumbs-toggle',
            defaultMessage: 'Show breadcrumbs for "{title}"',
          },
          { title },
        )}
        aria-expanded={isBreadcrumbsOpen}
      >
        <Box className="py-1">
          <FormattedMessage
            id="library.library_item_row_text"
            defaultMessage="Also appears in"
          />
        </Box>
        <Box className="ml-1 -translate-y-[2px]">
          <AnimatePresence>
            <motion.div
              animate={{
                rotate: isBreadcrumbsOpen ? '180deg' : '0deg',
              }}
              exit={{
                rotate: isBreadcrumbsOpen ? '180deg' : '0deg',
              }}
              transition={{ duration: 0.2 }}
            >
              <Box>
                <Icon icon={faChevronDown} className={`${text4} text-xs`} />
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Button>
    </>
  ) : null;

  const breadCrumbsList = showBreadcrumbsDropdown ? (
    <AnimatePresence initial={false}>
      {isBreadcrumbsOpen ? (
        <MotionDiv
          data-testid="item-row-breadcrumbs-container"
          style={{ overflow: 'hidden', marginTop: '10px' }}
          initial={{ height: isBreadcrumbsOpen ? 0 : 'auto' }}
          animate={{ height: isBreadcrumbsOpen ? 'auto' : 0 }}
          exit={{ height: isBreadcrumbsOpen ? 'auto' : 0 }}
          transition={{ duration: 0.2 }}
        >
          {breadcrumbsv2?.[1] && (
            <Box>
              <BreadcrumbsV2
                breadcrumb={breadcrumbsv2[1]}
                linkClassName="text-xs"
                onBreadCrumbItemClick={handleTrackBreadcrumb}
                title={title}
              />
            </Box>
          )}
        </MotionDiv>
      ) : null}
    </AnimatePresence>
  ) : null;

  const getCkeckboxLabes = (isChecked: boolean) => {
    return isChecked
      ? formatMessage({
          id: 'library.item-row.checkbox.completed',
          defaultMessage: 'Completed',
        })
      : formatMessage({
          id: 'library.item-row.checkbox.unchecked',
          defaultMessage: 'Mark as completed',
        });
  };

  return (
    <li
      data-testid="item-row-container"
      ref={ref}
      className={cn(
        'group relative w-full list-none rounded-lg px-2 py-4 hover:bg-zinc-100',
        liResponsiveClasses,
      )}
    >
      <Flex>
        <Flex className={`flex-col`}>
          <Flex>
            <A
              href={url}
              data-testid={`library-item-row-${title}-checkbox-text`}
              className={cn(
                'relative cursor-pointer',
                thumbnailLinkResponsiveClasses,
              )}
              tabIndex={-1}
            >
              {isInView ? (
                <ItemThumbnail
                  thumbnailURL={thumbnailUrl}
                  playIcon={playIcon}
                  minHeight="64px"
                  watchPercent={
                    learnWatchedItem ? learnWatchedItem.percent / 100 : 0
                  }
                  duration={duration}
                  imageSx={{
                    ...(isNoteThumbnail
                      ? {
                          padding: '6px',
                          maxWidth: '55px',
                          maxHeight: '64px',
                        }
                      : undefined),
                  }}
                  title={title}
                />
              ) : (
                <Box>
                  <Skeleton width={110} height={64} />
                </Box>
              )}
            </A>
            <Box
              className={`relative max-w-sm flex-1 cursor-pointer pl-4 pr-6`}
            >
              <Flex className={`w-full`}>
                {isInView ? (
                  <Box
                    className={`relative flex items-center`}
                    role="group"
                    aria-labelledby={`group-label-${title}`}
                  >
                    <Checkbox
                      label={getCkeckboxLabes(isChecked)}
                      labelClassName={`w-full items-center !gap-1 text-[10px] italic ${
                        isChecked ? textBlue : text4
                      } `}
                      className={`my-1 rounded-full`}
                      style={{ width: '10px', height: '10px' }}
                      checked={isChecked}
                      onChange={handleToggleCheckbox}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleToggleCheckbox();
                        }
                      }}
                      aria-label={formatMessage(
                        {
                          id: 'library.item-row.checkbox',
                          defaultMessage: '{action} video "{title}"',
                        },
                        { action: isChecked ? 'Unselect' : 'Select', title },
                      )}
                      data-testid={`library-item-row-${title}-checkbox`}
                    />
                  </Box>
                ) : (
                  <Box className={`relative mr-4 flex items-center`}>
                    <Skeleton width={20} height={20} />
                  </Box>
                )}
              </Flex>
              <span>
                <A
                  href={url}
                  data-testid="item-row-title"
                  className={`w-90 inline font-bold ${text5} !leading-4 no-underline group-hover:text-blue-600 sm:text-sm `}
                >
                  {title}
                </A>
                {lockedSubProduct ? (
                  <LockIcon variant={lockedSubProduct} />
                ) : null}
              </span>
              {isInView && (
                <>
                  <Flex
                    data-testid="item-row-breadcrumbs-toggler"
                    aria-expanded={isBreadcrumbsOpen}
                    onClick={handleBreadcrumbsClick}
                    className={`mt-0 cursor-pointer pl-2 hover:text-gray-500`}
                  >
                    {resourceItems.length ? (
                      <Flex className="items-center justify-center">
                        {resourceItems}
                      </Flex>
                    ) : null}
                    {breadcrumbsDropdown}
                  </Flex>
                  <section
                    className={breadcrumbsSectionDesktopClasses}
                    aria-label={formatMessage(
                      {
                        id: 'library.library-item-row.breadcrumbs-section',
                        defaultMessage: 'Breadcrumbs for "{title}"',
                      },
                      { title },
                    )}
                  >
                    {breadCrumbsList}
                  </section>
                </>
              )}
            </Box>
          </Flex>
          <section
            className={breadcrumbsSectionMobileClasses}
            aria-label={formatMessage(
              {
                id: 'library.library-item-row.breadcrumbs-section',
                defaultMessage: 'Breadcrumbs for "{title}"',
              },
              { title },
            )}
          >
            {breadCrumbsList}
          </section>
        </Flex>
      </Flex>
      {!toBeRetiredTag && (
        <Box className="opacity-0 focus-within:opacity-100 group-hover:!opacity-100">
          {playlistButton}
        </Box>
      )}
    </li>
  );
};

export default LibraryItemRow;
